import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface CustomOffer {
  id: string;
  user_id: string;
  created_by: string;
  base_plan: 'Starter' | 'Professional' | 'Agency';
  billing_cycle: 'monthly' | 'yearly';
  selected_addons: {
    seo?: boolean;
    saio?: boolean;
    performance?: boolean;
    social?: boolean;
    personas?: boolean;
  };
  calculated_price: number;
  final_price: number;
  discount_amount: number;
  discount_percentage: number;
  offer_code: string;
  expiration_date: string;
  status: 'active' | 'accepted' | 'expired' | 'revoked';
  viewed_at?: string;
  accepted_at?: string;
  stripe_subscription_id?: string;
  internal_notes?: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    email: string;
    full_name: string;
  };
  created_by_profile?: {
    email: string;
    full_name: string;
  };
}

export const useCustomOffer = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeOffer, setActiveOffer] = useState<CustomOffer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchActiveOffer = async () => {
    if (!user) {
      setActiveOffer(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // Expire old offers first
      await supabase.rpc('expire_custom_offers');

      const { data, error } = await supabase
        .from('custom_subscription_offers')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .gte('expiration_date', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        throw error;
      }

      // Fetch profile data separately if we have an offer
      if (data) {
        const [userProfile, creatorProfile] = await Promise.all([
          supabase.from('profiles').select('email, full_name').eq('id', data.user_id).maybeSingle(),
          supabase.from('profiles').select('email, full_name').eq('id', data.created_by).maybeSingle()
        ]);

        setActiveOffer({
          ...data,
          profiles: userProfile.data || undefined,
          created_by_profile: creatorProfile.data || undefined,
        } as CustomOffer);
      } else {
        setActiveOffer(null);
      }
    } catch (error: any) {
      console.error('Error fetching active offer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markOfferAsViewed = async (offerId: string) => {
    try {
      const { error } = await supabase
        .from('custom_subscription_offers')
        .update({ viewed_at: new Date().toISOString() })
        .eq('id', offerId)
        .is('viewed_at', null);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking offer as viewed:', error);
    }
  };

  useEffect(() => {
    fetchActiveOffer();
  }, [user]);

  // Mark as viewed when offer is loaded
  useEffect(() => {
    if (activeOffer && !activeOffer.viewed_at) {
      markOfferAsViewed(activeOffer.id);
    }
  }, [activeOffer]);

  return {
    activeOffer,
    isLoading,
    refetch: fetchActiveOffer,
  };
};
